// SuperChargeMe Data Platform - Modern React frontend implementation
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import axios from 'axios';

// Unified design theme (tokens + MUI overrides)
import { theme } from './theme';

// Components
import Login from './components/auth/Login';
import ForgotPassword from './components/auth/ForgotPassword';
import ResetPassword from './components/auth/ResetPassword';
import AcceptInvite from './components/auth/AcceptInvite';
import Dashboard from './components/dashboard/Dashboard';
import DashboardReal from './components/dashboard/DashboardReal';
import BrandManagement from './components/brands/BrandManagement';
import SubsidiaryManagement from './components/subsidiaries/SubsidiaryManagement';
import ConnectorSetup from './components/connectors/ConnectorSetup';
import MiraklFeedHealth from './components/mirakl/MiraklFeedHealth';
import TeamsSetupGuide from './components/connectors/TeamsSetupGuide';
import ChatInterface from './components/chat/ChatInterface';
import { IN_APP_CHAT_ENABLED } from './config/modules';
import Layout from './components/layout/Layout';
import ExpertsPage from './components/experts/ExpertsPage';
import PrivateRoute from './components/auth/PrivateRoute';
import HomeRedirect from './components/auth/HomeRedirect';
import Visualizations from './components/visualizations/Visualizations';
import UserProfile from './components/auth/UserProfile';
import AdminPanel from './components/admin/AdminPanel';
import AuditLogViewer from './components/admin/AuditLogViewer';
import ActionQueue from './components/operations/ActionQueue';
import AutomationDashboard from './components/automation/AutomationDashboard';
import ScheduledJobsPage from './components/scheduled-jobs/ScheduledJobsPage';
import GoogleSheetsManager from './components/google-sheets/GoogleSheetsManager';
import OAuthCallback from './components/connectors/OAuthCallback';
import GoogleSearchConsoleSelection from './components/connectors/GoogleSearchConsoleSelection';
import GooglePostmasterSelection from './components/connectors/GooglePostmasterSelection';
import GoogleAnalyticsSelection from './components/connectors/GoogleAnalyticsSelection';
import GoogleAdsSelection from './components/connectors/GoogleAdsSelection';
import GoogleMerchantCenterSelection from './components/connectors/GoogleMerchantCenterSelection';
import FacebookAccountSelection from './components/connectors/FacebookAccountSelection';
import FacebookPagesManager from './components/connectors/FacebookPagesManager';
import FacebookReauthConfirm from './components/connectors/FacebookReauthConfirm';
import TikTokAccountSelection from './components/connectors/TikTokAccountSelection';
import SnapchatAdsAccountSelection from './components/connectors/SnapchatAdsAccountSelection';
import TikTokShopSelection from './components/connectors/TikTokShopSelection';
import PinterestProfileSelection from './components/connectors/PinterestProfileSelection';
import TikTokShopSuccess from './components/connectors/TikTokShopSuccess';
import TikTokAdsSuccess from './components/connectors/TikTokAdsSuccess';
import QuickBooksSelection from './components/connectors/QuickBooksSelection';
import XeroSelection from './components/connectors/XeroSelection';
import MicrosoftAdsSelection from './components/connectors/MicrosoftAdsSelection';
import RedditAdsSelection from './components/connectors/RedditAdsSelection';
import Sa360Selection from './components/connectors/Sa360Selection';
import Dv360Selection from './components/connectors/Dv360Selection';
import Cm360Selection from './components/connectors/Cm360Selection';
import PrivacyPolicy from './components/legal/PrivacyPolicy';
import TermsOfService from './components/legal/TermsOfService';
import Support from './components/legal/Support';
import Settings from './components/legal/Settings';
import Pricing from './components/legal/Pricing';
import Register from './components/auth/Register';

// Alert Preferences Components
import AlertPreferences from './components/settings/AlertPreferences';
import AlertRulesBuilder from './components/settings/AlertRulesBuilder';

// Inventory Management Components
import InventoryDashboard from './components/inventory/InventoryDashboard';
import PriceTierAdmin from './components/price-tiers/PriceTierAdmin';
import CommercialInvoices from './components/commercial-invoices/CommercialInvoices';
import ReadyToInvoice from './components/wholesale-orders/ReadyToInvoice';
import WholesaleOrderEntry from './components/wholesale-orders/WholesaleOrderEntry';
import PendingShip from './components/wholesale-orders/PendingShip';
import CustomersAdmin from './components/wholesale-orders/CustomersAdmin';
import CustomerHistory from './components/wholesale-orders/CustomerHistory';
import BankReconciliation from './components/wholesale-orders/BankReconciliation';
import Cin7PortConfigAdmin from './components/cin7-port-config/Cin7PortConfigAdmin';
import StockLevels from './components/inventory/StockLevels';
import BatchManagement from './components/inventory/BatchManagement';
import ProductCOGS from './components/inventory/ProductCOGS';
import CategoryManagement from './components/inventory/CategoryManagement';
import BomCatalog from './components/inventory/BomCatalog';
import PackagingConsumables from './components/inventory/PackagingConsumables';
import WorkOrders from './components/inventory/WorkOrders';
import AdminUnresolved from './components/inventory/AdminUnresolved';
import RetailOrders from './components/inventory/RetailOrders';
import Warehouses from './components/inventory/Warehouses';
import WarehouseBrandSubsidiaryMatrix from './components/inventory/WarehouseBrandSubsidiaryMatrix';
import WarehouseMappings from './components/inventory/WarehouseMappings';
import FbaCapacityLimits from './components/inventory/FbaCapacityLimits';
import Transfers from './components/inventory/Transfers';
import WarehouseTransferEntry from './components/inventory/WarehouseTransferEntry';
import Returns from './components/inventory/Returns';
import Disposals from './components/inventory/Disposals';
import PhysicalCounts from './components/inventory/physical-counts';
import TrustPointWizard from './components/inventory/TrustPointWizard';
import Adjustments from './components/inventory/Adjustments';
import WarehouseBreakdown from './components/inventory/WarehouseBreakdown';
import InventoryMovements from './components/inventory/InventoryMovements';
import AsinMapping from './components/inventory/AsinMapping';
import FbaShipmentDiscrepancies from './components/inventory/FbaShipmentDiscrepancies';
import InventorySummary from './components/inventory/InventorySummary';
import AmazonRestockRecommendations from './components/inventory/AmazonRestockRecommendations';

// Reports
import InventoryMasterReport from './components/reports/InventoryMasterReport';
import InventoryRunRateReport from './components/reports/InventoryRunRateReport';

// Procurement Management Components
import ProcurementDashboard from './components/procurement/ProcurementDashboard';
import Suppliers from './components/procurement/Suppliers';
import PurchaseOrders from './components/procurement/PurchaseOrders';
import PurchaseOrderEntry from './components/procurement/PurchaseOrderEntry';
import GoodsReceipts from './components/procurement/GoodsReceipts';
import ImportedDraftsReview from './components/procurement/ImportedDraftsReview';
import Invoices from './components/procurement/Invoices';
import Payments from './components/procurement/Payments';

// Retail Management Components
import RetailDashboard from './components/retail/RetailDashboard';

// Contacts hub
import Contacts from './components/contacts/Contacts';

// Call Centre Components
import CallCentreDashboard from './components/call-centre/CallCentreDashboard';
import AgentLogin from './components/call-centre/AgentLogin';
import AgentAuthCallback from './components/call-centre/AgentAuthCallback';

// Finance Management Components
import FinanceDashboard from './components/finance/FinanceDashboard';
import CashPosition from './components/finance/CashPosition';
import ARAnalysis from './components/finance/ARAnalysis';
import CreditNotes from './components/finance/CreditNotes';
import APAnalysis from './components/finance/APAnalysis';
import FinanceSearch from './components/finance/FinanceSearch';
import IntercompanyView from './components/finance/IntercompanyView';
import ReconciliationView from './components/finance/ReconciliationView';
import ReconciliationHub from './components/finance/ReconciliationHub';
import ConfigurationSettings from './components/finance/ConfigurationSettings';
import PLReport from './components/finance/PLReport';
import BalanceSheet from './components/finance/BalanceSheet';
import CashFlowStatement from './components/finance/CashFlowStatement';
import CashFlowProjections from './components/finance/CashFlowProjections';
import ProcessorBalances from './components/finance/ProcessorBalances';
import GoodsInTransit from './components/finance/GoodsInTransit';
import FinancialStatements from './components/finance/FinancialStatements';
import BankGLMappings from './components/finance/BankGLMappings';
import PeriodsManager from './components/finance/PeriodsManager';
import DataSources from './components/finance/DataSources';
import VATVendorReview from './components/finance/VATVendorReview';
import TaxReturns from './components/finance/TaxReturns';

// Styles
import './App.css';

// Auth Context
import { AuthProvider } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';
import UsageTracker from './components/UsageTracker';


// API setup
axios.defaults.baseURL = process.env.REACT_APP_API_URL || 'https://api.superchargeme.com';
axios.interceptors.request.use(
  (config) => {
    // Both headers from the CURRENT token on every request (the backend reads x-auth-token first), so a header from an
    // earlier session never outlives a logout or another user's sign-in in the same tab (DT-1521).
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      config.headers['x-auth-token'] = token;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

function App() {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Fonts are preloaded from index.html — just run the boot splash timer.
    const timer = setTimeout(() => setIsLoading(false), 1000);
    return () => clearTimeout(timer);
  }, []);

  if (isLoading) {
    return (
      <div className="loading-screen">
        <div className="superchargeme-logo">
          <h1>SUPERCHARGEME</h1>
          <p>Unified Data Platform</p>
        </div>
        <div className="pulse-loader"></div>
      </div>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ToastProvider>
      <AuthProvider>
        <Router>
          <UsageTracker />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/accept-invite" element={<AcceptInvite />} />
            <Route path="/" element={<PrivateRoute><HomeRedirect><Layout><Dashboard /></Layout></HomeRedirect></PrivateRoute>} />
            <Route path="/brands" element={<PrivateRoute><Layout><BrandManagement /></Layout></PrivateRoute>} />
            <Route path="/subsidiaries" element={<PrivateRoute><Layout><SubsidiaryManagement /></Layout></PrivateRoute>} />
            <Route path="/connectors" element={<PrivateRoute><Layout><ConnectorSetup /></Layout></PrivateRoute>} />
            <Route path="/mirakl-feeds" element={<PrivateRoute><Layout><MiraklFeedHealth /></Layout></PrivateRoute>} />
            <Route path="/connectors/teams/setup" element={<PrivateRoute><Layout><TeamsSetupGuide /></Layout></PrivateRoute>} />
            <Route path="/automation" element={<PrivateRoute><Layout><AutomationDashboard /></Layout></PrivateRoute>} />
            <Route path="/chat" element={IN_APP_CHAT_ENABLED ? <PrivateRoute><Layout><ChatInterface /></Layout></PrivateRoute> : <Navigate to="/" replace />} />
            {(process.env.REACT_APP_FEATURE_EXPERTS ?? 'true') === 'true' && (
              <Route path="/experts" element={<PrivateRoute><Layout><ExpertsPage /></Layout></PrivateRoute>} />
            )}
            <Route path="/scheduled-jobs" element={<PrivateRoute><Layout><ScheduledJobsPage /></Layout></PrivateRoute>} />
            <Route path="/contacts" element={<PrivateRoute><Layout><Contacts /></Layout></PrivateRoute>} />
            <Route path="/google-sheets" element={<PrivateRoute><Layout><GoogleSheetsManager /></Layout></PrivateRoute>} />
            <Route path="/visualizations" element={<PrivateRoute><Layout><Visualizations /></Layout></PrivateRoute>} />
            <Route path="/profile" element={<PrivateRoute><Layout><UserProfile /></Layout></PrivateRoute>} />
            <Route path="/admin" element={<PrivateRoute><Layout><AdminPanel /></Layout></PrivateRoute>} />
            <Route path="/oauth/shopify/callback" element={<OAuthCallback />} />
            <Route path="/oauth/facebook/callback" element={<OAuthCallback />} />
            <Route path="/oauth/amazon-ads/callback" element={<OAuthCallback />} />
            <Route path="/oauth/amazon-sp/callback" element={<OAuthCallback />} />
            <Route path="/oauth/google-ads/callback" element={<OAuthCallback />} />
            <Route path="/oauth/tiktok/callback" element={<OAuthCallback />} />
            <Route path="/oauth/tiktok-shop/callback" element={<OAuthCallback />} />
            {/* Non-hyphen alias — matches the redirect URI registered in TikTok Partner
                Center (/oauth/tiktokshop/callback). Both resolve to the same handler. */}
            <Route path="/oauth/tiktokshop/callback" element={<OAuthCallback />} />
            {/* PUBLIC success page for the shared-link flow — a not-logged-in client
                lands here after the callback auto-provisions their connector. Must stay
                outside PrivateRoute (the client has no SuperChargeMe login). */}
            <Route path="/oauth/tiktokshop/success" element={<TikTokShopSuccess />} />
            <Route path="/oauth/tiktok-shop/success" element={<TikTokShopSuccess />} />
            {/* TikTok Ads client-link completion — same public, no-login contract. */}
            <Route path="/oauth/tiktok/success" element={<TikTokAdsSuccess />} />
            <Route path="/oauth/tiktok-creator/callback" element={<OAuthCallback />} />
            <Route path="/oauth/slack/callback" element={<OAuthCallback />} />
            <Route path="/oauth/teams/callback" element={<OAuthCallback />} />
            <Route path="/oauth/klaviyo/callback" element={<OAuthCallback />} />
            <Route path="/oauth/pinterest/callback" element={<OAuthCallback />} />
            <Route path="/oauth/google-search-console/callback" element={<OAuthCallback />} />
            <Route path="/oauth/google-analytics/callback" element={<OAuthCallback />} />
            <Route path="/oauth/google-merchant-center/callback" element={<OAuthCallback />} />
            <Route path="/oauth/google-postmaster/callback" element={<OAuthCallback />} />
            <Route path="/oauth/quickbooks/callback" element={<OAuthCallback />} />
            <Route path="/oauth/xero/callback" element={<OAuthCallback />} />
            <Route path="/oauth/revolut/callback" element={<OAuthCallback />} />
            <Route path="/oauth/wise/callback" element={<OAuthCallback />} />
            <Route path="/oauth/microsoft-ads/callback" element={<OAuthCallback />} />
            <Route path="/oauth/attentive/callback" element={<OAuthCallback />} />
            <Route path="/oauth/google-workspace/callback" element={<OAuthCallback />} />
            <Route path="/oauth/microsoft-workspace/callback" element={<OAuthCallback />} />
            <Route path="/oauth/sps-commerce/callback" element={<OAuthCallback />} />
            {/* Lightspeed Retail (R-Series) OAuth — PUBLIC (the client authorizing has no
                SuperChargeMe login). Path matches the redirect_uri registered on the
                R-Series API client. Provider slug parsed by OAuthCallback = 'lightspeed'. */}
            <Route path="/oauth/lightspeed/callback" element={<OAuthCallback />} />
            <Route path="/oauth/square/callback" element={<OAuthCallback />} />
            <Route path="/oauth/business-central/callback" element={<OAuthCallback />} />
            <Route path="/oauth/snapchat-ads/callback" element={<OAuthCallback />} />
            <Route path="/oauth/reddit-ads/callback" element={<OAuthCallback />} />
            <Route path="/oauth/sa360/callback" element={<OAuthCallback />} />
            <Route path="/oauth/amazon-vendor/callback" element={<OAuthCallback />} />
            <Route path="/oauth/dv360/callback" element={<OAuthCallback />} />
            <Route path="/oauth/cm360/callback" element={<OAuthCallback />} />
            <Route path="/connectors/google-search-console/select" element={<PrivateRoute><Layout><GoogleSearchConsoleSelection /></Layout></PrivateRoute>} />
            <Route path="/connectors/google-postmaster/select" element={<PrivateRoute><Layout><GooglePostmasterSelection /></Layout></PrivateRoute>} />
            <Route path="/connectors/google-analytics/select" element={<PrivateRoute><Layout><GoogleAnalyticsSelection /></Layout></PrivateRoute>} />
            <Route path="/connectors/google-ads/select" element={<PrivateRoute><Layout><GoogleAdsSelection /></Layout></PrivateRoute>} />
            <Route path="/connectors/google-merchant-center/select" element={<PrivateRoute><Layout><GoogleMerchantCenterSelection /></Layout></PrivateRoute>} />
            <Route path="/connectors/facebook/select" element={<PrivateRoute><Layout><FacebookAccountSelection /></Layout></PrivateRoute>} />
            <Route path="/connectors/facebook/manage" element={<PrivateRoute><Layout><FacebookPagesManager /></Layout></PrivateRoute>} />
            <Route path="/connectors/facebook/reauth-confirm" element={<PrivateRoute><Layout><FacebookReauthConfirm /></Layout></PrivateRoute>} />
            <Route path="/connectors/tiktok/select" element={<PrivateRoute><Layout><TikTokAccountSelection /></Layout></PrivateRoute>} />
            <Route path="/connectors/snapchat-ads/select" element={<PrivateRoute><Layout><SnapchatAdsAccountSelection /></Layout></PrivateRoute>} />
            <Route path="/connectors/tiktok-shop/select" element={<PrivateRoute><Layout><TikTokShopSelection /></Layout></PrivateRoute>} />
            <Route path="/connectors/pinterest/select" element={<PrivateRoute><Layout><PinterestProfileSelection /></Layout></PrivateRoute>} />
            <Route path="/connectors/quickbooks/select" element={<PrivateRoute><Layout><QuickBooksSelection /></Layout></PrivateRoute>} />
            <Route path="/connectors/xero/select" element={<PrivateRoute><Layout><XeroSelection /></Layout></PrivateRoute>} />
            <Route path="/connectors/microsoft-ads/select" element={<PrivateRoute><Layout><MicrosoftAdsSelection /></Layout></PrivateRoute>} />
            <Route path="/connectors/reddit-ads/select" element={<PrivateRoute><Layout><RedditAdsSelection /></Layout></PrivateRoute>} />
            <Route path="/connectors/sa360/select" element={<PrivateRoute><Layout><Sa360Selection /></Layout></PrivateRoute>} />
            <Route path="/connectors/dv360/select" element={<PrivateRoute><Layout><Dv360Selection /></Layout></PrivateRoute>} />
            <Route path="/connectors/cm360/select" element={<PrivateRoute><Layout><Cm360Selection /></Layout></PrivateRoute>} />

            {/* Inventory Management Routes */}
            <Route path="/inventory" element={<PrivateRoute><Layout><InventoryDashboard /></Layout></PrivateRoute>} />
            <Route path="/inventory/summary" element={<PrivateRoute><Layout><InventorySummary /></Layout></PrivateRoute>} />
            <Route path="/inventory/stock" element={<PrivateRoute><Layout><StockLevels /></Layout></PrivateRoute>} />
            <Route path="/inventory/batches" element={<PrivateRoute><Layout><BatchManagement /></Layout></PrivateRoute>} />
            <Route path="/inventory/cogs" element={<PrivateRoute><Layout><ProductCOGS /></Layout></PrivateRoute>} />
            <Route path="/inventory/categories" element={<PrivateRoute><Layout><CategoryManagement /></Layout></PrivateRoute>} />
            <Route path="/inventory/boms" element={<PrivateRoute><Layout><BomCatalog /></Layout></PrivateRoute>} />
            <Route path="/inventory/packaging" element={<PrivateRoute><Layout><PackagingConsumables /></Layout></PrivateRoute>} />
            <Route path="/inventory/work-orders" element={<PrivateRoute><Layout><WorkOrders /></Layout></PrivateRoute>} />
            <Route path="/inventory/cin7-unresolved" element={<PrivateRoute><Layout><AdminUnresolved /></Layout></PrivateRoute>} />
            <Route path="/inventory/price-tiers" element={<PrivateRoute><Layout><PriceTierAdmin /></Layout></PrivateRoute>} />
            <Route path="/inventory/commercial-invoices" element={<PrivateRoute><Layout><CommercialInvoices /></Layout></PrivateRoute>} />
            <Route path="/wholesale/ready-to-invoice" element={<PrivateRoute><Layout><ReadyToInvoice /></Layout></PrivateRoute>} />
            <Route path="/wholesale/new" element={<PrivateRoute><Layout><WholesaleOrderEntry /></Layout></PrivateRoute>} />
            <Route path="/wholesale/pending-ship" element={<PrivateRoute><Layout><PendingShip /></Layout></PrivateRoute>} />
            <Route path="/retail/bank-reconciliation" element={<PrivateRoute><Layout><BankReconciliation /></Layout></PrivateRoute>} />
            <Route path="/retail/customers" element={<PrivateRoute><Layout><CustomersAdmin /></Layout></PrivateRoute>} />
            <Route path="/retail/customers/:qbCustomerId/history" element={<PrivateRoute><Layout><CustomerHistory /></Layout></PrivateRoute>} />
            <Route path="/admin/cin7-port-config" element={<PrivateRoute><Layout><Cin7PortConfigAdmin /></Layout></PrivateRoute>} />
            <Route path="/inventory/asin-mapping" element={<PrivateRoute><Layout><AsinMapping /></Layout></PrivateRoute>} />
            <Route path="/inventory/retail-orders" element={<PrivateRoute><Layout><RetailOrders /></Layout></PrivateRoute>} />
            <Route path="/inventory/warehouse-breakdown" element={<PrivateRoute><Layout><WarehouseBreakdown /></Layout></PrivateRoute>} />
            <Route path="/inventory/movements" element={<PrivateRoute><Layout><InventoryMovements /></Layout></PrivateRoute>} />
            
            {/* Reports */}
            <Route path="/reports/inventory-master" element={<PrivateRoute><Layout><InventoryMasterReport /></Layout></PrivateRoute>} />
            <Route path="/reports/run-rates" element={<PrivateRoute><Layout><InventoryRunRateReport /></Layout></PrivateRoute>} />
            <Route path="/inventory/warehouses" element={<PrivateRoute><Layout><Warehouses /></Layout></PrivateRoute>} />
            <Route path="/inventory/warehouses/:id" element={<PrivateRoute><Layout><Warehouses /></Layout></PrivateRoute>} />
            <Route path="/inventory/warehouse-brand-subsidiary" element={<PrivateRoute><Layout><WarehouseBrandSubsidiaryMatrix /></Layout></PrivateRoute>} />
            <Route path="/admin/audit-log" element={<PrivateRoute><Layout><AuditLogViewer /></Layout></PrivateRoute>} />
            <Route path="/operations" element={<PrivateRoute><Layout><ActionQueue /></Layout></PrivateRoute>} />
            <Route path="/inventory/transfers" element={<PrivateRoute><Layout><Transfers /></Layout></PrivateRoute>} />
            <Route path="/inventory/transfers/new" element={<PrivateRoute><Layout><WarehouseTransferEntry /></Layout></PrivateRoute>} />
            <Route path="/inventory/transfers/:id/edit" element={<PrivateRoute><Layout><WarehouseTransferEntry /></Layout></PrivateRoute>} />
            <Route path="/inventory/transfers/:id" element={<PrivateRoute><Layout><Transfers /></Layout></PrivateRoute>} />
            <Route path="/inventory/returns" element={<PrivateRoute><Layout><Returns /></Layout></PrivateRoute>} />
            <Route path="/inventory/returns/:id" element={<PrivateRoute><Layout><Returns /></Layout></PrivateRoute>} />
            <Route path="/inventory/counts" element={<PrivateRoute><Layout><PhysicalCounts /></Layout></PrivateRoute>} />
            <Route path="/inventory/counts/:id" element={<PrivateRoute><Layout><PhysicalCounts /></Layout></PrivateRoute>} />
            <Route path="/inventory/trust-point-wizard" element={<PrivateRoute><Layout><TrustPointWizard /></Layout></PrivateRoute>} />
            <Route path="/inventory/mappings" element={<PrivateRoute><Layout><WarehouseMappings /></Layout></PrivateRoute>} />
            <Route path="/inventory/fba-capacity" element={<PrivateRoute><Layout><FbaCapacityLimits /></Layout></PrivateRoute>} />
            <Route path="/inventory/disposals" element={<PrivateRoute><Layout><Disposals /></Layout></PrivateRoute>} />
            <Route path="/inventory/adjustments" element={<PrivateRoute><Layout><Adjustments /></Layout></PrivateRoute>} />
            <Route path="/inventory/fba-discrepancies" element={<PrivateRoute><Layout><FbaShipmentDiscrepancies /></Layout></PrivateRoute>} />
            <Route path="/inventory/amazon-restock" element={<PrivateRoute><Layout><AmazonRestockRecommendations /></Layout></PrivateRoute>} />

            {/* Procurement Management Routes */}
            <Route path="/procurement" element={<PrivateRoute><Layout><ProcurementDashboard /></Layout></PrivateRoute>} />
            <Route path="/procurement/suppliers" element={<PrivateRoute><Layout><Suppliers /></Layout></PrivateRoute>} />
            <Route path="/procurement/suppliers/:id" element={<PrivateRoute><Layout><Suppliers /></Layout></PrivateRoute>} />
            <Route path="/procurement/purchase-orders" element={<PrivateRoute><Layout><PurchaseOrders /></Layout></PrivateRoute>} />
            <Route path="/procurement/purchase-orders/new" element={<PrivateRoute><Layout><PurchaseOrderEntry /></Layout></PrivateRoute>} />
            <Route path="/procurement/purchase-orders/:id/edit" element={<PrivateRoute><Layout><PurchaseOrderEntry /></Layout></PrivateRoute>} />
            <Route path="/procurement/purchase-orders/:id" element={<PrivateRoute><Layout><PurchaseOrders /></Layout></PrivateRoute>} />
            <Route path="/procurement/goods-receipts" element={<PrivateRoute><Layout><GoodsReceipts /></Layout></PrivateRoute>} />
            <Route path="/procurement/goods-receipts/:id" element={<PrivateRoute><Layout><GoodsReceipts /></Layout></PrivateRoute>} />
            <Route path="/procurement/imported-receipts" element={<PrivateRoute><Layout><ImportedDraftsReview /></Layout></PrivateRoute>} />
            <Route path="/procurement/invoices" element={<PrivateRoute><Layout><Invoices /></Layout></PrivateRoute>} />
            <Route path="/procurement/invoices/:id" element={<PrivateRoute><Layout><Invoices /></Layout></PrivateRoute>} />
            <Route path="/procurement/payments" element={<PrivateRoute><Layout><Payments /></Layout></PrivateRoute>} />
            <Route path="/procurement/payments/:id" element={<PrivateRoute><Layout><Payments /></Layout></PrivateRoute>} />

            {/* Retail Management Routes */}
            <Route path="/retail" element={<PrivateRoute><Layout><RetailDashboard /></Layout></PrivateRoute>} />
            <Route path="/retail/orders" element={<PrivateRoute><Layout><RetailOrders /></Layout></PrivateRoute>} />
            <Route path="/retail/orders/:id" element={<PrivateRoute><Layout><RetailOrders /></Layout></PrivateRoute>} />

            {/* Finance Management Routes */}
            <Route path="/finance" element={<PrivateRoute><Layout><FinanceDashboard /></Layout></PrivateRoute>} />
            <Route path="/finance/cash" element={<PrivateRoute><Layout><CashPosition /></Layout></PrivateRoute>} />
            <Route path="/finance/ar" element={<PrivateRoute><Layout><ARAnalysis /></Layout></PrivateRoute>} />
            <Route path="/finance/credit-notes" element={<PrivateRoute><Layout><CreditNotes /></Layout></PrivateRoute>} />
            <Route path="/finance/ap" element={<PrivateRoute><Layout><APAnalysis /></Layout></PrivateRoute>} />
            <Route path="/finance/intercompany" element={<PrivateRoute><Layout><IntercompanyView /></Layout></PrivateRoute>} />
            <Route path="/finance/reconciliation" element={<PrivateRoute><Layout><ReconciliationView /></Layout></PrivateRoute>} />
            <Route path="/finance/journal-reconciliation" element={<PrivateRoute><Layout><ReconciliationHub /></Layout></PrivateRoute>} />
            {/* Single P&L page on accrual basis (2026-07-10: cash page retired).
                /finance/pl-accrual redirects for old bookmarks/links. */}
            <Route path="/finance/pl" element={<PrivateRoute><Layout><PLReport key="pl" basis="accrual" /></Layout></PrivateRoute>} />
            <Route path="/finance/pl-accrual" element={<Navigate to="/finance/pl" replace />} />
            <Route path="/finance/balance-sheet" element={<PrivateRoute><Layout><BalanceSheet /></Layout></PrivateRoute>} />
            <Route path="/finance/cash-flow" element={<PrivateRoute><Layout><CashFlowStatement /></Layout></PrivateRoute>} />
            <Route path="/finance/cash-flow-projections" element={<PrivateRoute><Layout><CashFlowProjections /></Layout></PrivateRoute>} />
            <Route path="/finance/processor-balances" element={<PrivateRoute><Layout><ProcessorBalances /></Layout></PrivateRoute>} />
            <Route path="/finance/goods-in-transit" element={<PrivateRoute><Layout><GoodsInTransit /></Layout></PrivateRoute>} />
            <Route path="/finance/settings" element={<PrivateRoute><Layout><ConfigurationSettings /></Layout></PrivateRoute>} />
            <Route path="/finance/statements" element={<PrivateRoute><Layout><FinancialStatements /></Layout></PrivateRoute>} />
            <Route path="/finance/bank-gl-mappings" element={<PrivateRoute><Layout><BankGLMappings /></Layout></PrivateRoute>} />
            <Route path="/finance/periods" element={<PrivateRoute><Layout><PeriodsManager /></Layout></PrivateRoute>} />
            <Route path="/finance/data-sources" element={<PrivateRoute><Layout><DataSources /></Layout></PrivateRoute>} />
            <Route path="/finance/vat-vendors" element={<PrivateRoute><Layout><VATVendorReview /></Layout></PrivateRoute>} />
            <Route path="/finance/search" element={<PrivateRoute><Layout><FinanceSearch /></Layout></PrivateRoute>} />
            <Route path="/finance/counterparty/:counterpartyKey" element={<PrivateRoute><Layout><FinanceSearch /></Layout></PrivateRoute>} />
            <Route path="/finance/tax-returns" element={<PrivateRoute><Layout><TaxReturns /></Layout></PrivateRoute>} />
            <Route path="/finance/vat-return" element={<PrivateRoute><Layout><TaxReturns initialTab={0} /></Layout></PrivateRoute>} />
            <Route path="/finance/bas-return" element={<PrivateRoute><Layout><TaxReturns initialTab={1} /></Layout></PrivateRoute>} />

            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/support" element={<Support />} />
            <Route path="/settings" element={<PrivateRoute><Layout><Settings /></Layout></PrivateRoute>} />
            <Route path="/settings/alerts" element={<PrivateRoute><Layout><AlertPreferences /></Layout></PrivateRoute>} />
            <Route path="/settings/alert-rules" element={<PrivateRoute><Layout><AlertRulesBuilder /></Layout></PrivateRoute>} />

            {/* Call Centre - Uses Slack authentication, not regular login */}
            <Route path="/call-centre" element={<CallCentreDashboard />} />
            <Route path="/call-centre/login" element={<AgentLogin />} />
            <Route path="/call-centre/auth/callback" element={<AgentAuthCallback />} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
      </AuthProvider>
      </ToastProvider>
    </ThemeProvider>
  );
}

export default App;// Build time: Tue May 27 04:43:37 PM CEST 2025
